<script setup lang="ts">
/**
 * ShareBetSlipDialog
 *
 * Dialog container that displays the share flow for BetSlip content.
 * Supports sharing by single conid or productConid.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import BetSlipByConid from './BetSlip/BetSlipByConid.vue';
import BetSlipByProduct from './BetSlip/BetSlipByProduct.vue';
import ShareBetSlip from './BetSlip/ShareBetSlip.vue';
import { useUserState } from '@/stores/useUserState';
import { determineBetSlipMode } from './BetSlip/utils';
import { buildShareEmailHtml, copyHtmlToClipboard } from '@/utils/shareEmailHtml';

const { t } = useI18n();
const userState = useUserState();

const props = defineProps<{
  /** Whether the dialog is visible */
  visible: boolean;
  /** Single contract ID (takes precedence over productConid) */
  conid?: number;
  /** Election product ID */
  productConid?: number;
}>();

const emit = defineEmits<{
  'update:visible': [value: boolean];
  /** Emitted after successful share */
  success: [];
}>();

// Determine which mode we're in (conid takes precedence)
const mode = computed(() => determineBetSlipMode(props.conid, props.productConid));

// Refs to wrapper components for accessing exposed data
const betSlipByConidRef = ref<InstanceType<typeof BetSlipByConid>>();
const betSlipByProductRef = ref<InstanceType<typeof BetSlipByProduct>>();

// Market name from wrapper data
const marketName = computed(() => {
  if (mode.value === 'conid' && betSlipByConidRef.value?.marketName) {
    return betSlipByConidRef.value.marketName;
  }
  if (mode.value === 'product' && betSlipByProductRef.value?.marketName) {
    return betSlipByProductRef.value.marketName;
  }
  return 'Election Contract';
});

// Get items from wrapper data for the share component
const shareItems = computed(() => {
  if (mode.value === 'conid' && betSlipByConidRef.value?.betSlipProps?.items) {
    return betSlipByConidRef.value.betSlipProps.items;
  }
  if (mode.value === 'product' && betSlipByProductRef.value?.betSlipProps?.items) {
    return betSlipByProductRef.value.betSlipProps.items;
  }
  return [];
});

// Get betSlipLink from the active wrapper
const betSlipLink = computed(() => {
  if (mode.value === 'conid') return betSlipByConidRef.value?.betSlipLink || '';
  if (mode.value === 'product') return betSlipByProductRef.value?.betSlipLink || '';
  return '';
});

// PrimeVue Dialog pass-through
const DIALOG_PT = {
  header: { class: 'border-b border-outline-variant !px-6 !py-4 bg-surface-low' },
  content: { class: '!p-0' },
  pcCloseButton: { root: { class: '!visible !hidden' } },
};

// Square CTA buttons for email preview – match the #ACFAEA green used in the copied HTML
const CTA_BUTTON_PT = {
  root: {
    class: '!rounded-none !py-2 !px-3 !text-sm !border-none',
    style: 'background-color: #ACFAEA !important; color: #111827 !important;',
  },
};

const isCopyingPreview = ref(false);
const copyPreviewSuccess = ref(false);

async function handleCopyPreview() {
  isCopyingPreview.value = true;
  copyPreviewSuccess.value = false;

  const dto = {
    title: marketName.value,
    betSlipLink: betSlipLink.value,
    appName: 'FORECASTTRADER',
    items: shareItems.value.map((item, index) => ({
      title: item.title,
      titleColor: item.titleColor || '',
      landPageLink: item.link || '',
      imageLink: item.image || '',
      displayOrder: index + 1,
      chips: item.chips.map((chip, chipIndex) => ({
        label: chip.label,
        displayOrder: chipIndex + 1,
      })),
    })),
  };

  const html = buildShareEmailHtml(dto);
  const success = await copyHtmlToClipboard(html);
  isCopyingPreview.value = false;

  if (success) {
    copyPreviewSuccess.value = true;
    window.$apm?.logUserInteractionEvent('Share Copy Email Body', 'copy-email-body', {}, false);
    setTimeout(() => {
      copyPreviewSuccess.value = false;
    }, 2000);
  }
}

function handleClose() {
  emit('update:visible', false);
}

function handleSuccess() {
  emit('success');
  handleClose();
}

function login() {
  const domain = window.location.host.replace(/^[^.]+\./g, '');
  window.location.href = `https://www.${domain}/sso/Login?forwardTo=100`;
}

function signup() {
  const domain = window.location.host.replace(/^[^.]+\./g, '');
  window.location.href = `https://www.${domain}/Universal/Application?eventContract=T`;
}
</script>

<template>
  <Dialog
    :visible="visible"
    modal
    :closable="false"
    :pt="DIALOG_PT"
    class="share-betslip-dialog"
    @update:visible="emit('update:visible', $event)"
  >
    <template #header>
      <div class="flex w-full items-center justify-between">
        <h4 class="font-bold text-on-surface">{{ t('share') }}</h4>
        <div class="flex items-center gap-2">
          <!-- Log In / Open Account links for unauthenticated users -->
          <template v-if="!userState.authenticated">
            <button class="text-sm font-medium text-primary hover:underline cursor-pointer" @click="login">
              {{ t('logIn') }}
            </button>
            <span class="text-on-surface-tertiary">|</span>
            <button class="text-sm font-medium text-primary hover:underline cursor-pointer" @click="signup">
              <span data-type="cta">{{ t('openAccount') }}</span>
            </button>
          </template>
          <button
            class="flex items-center justify-center w-8 h-8 rounded-full hover:bg-surface-hover text-on-surface-variant cursor-pointer"
            :aria-label="t('closeDialog')"
            @click="handleClose"
          >
            <i class="ibi-close text-base" />
          </button>
        </div>
      </div>
    </template>

    <div class="ratio-design-provider">
      <!-- Error state when no params provided -->
      <div v-if="mode === 'none'" class="text-center py-8">
        <p class="text-on-surface-variant">{{ t('share_no_contract') }}</p>
      </div>

      <!-- Content based on mode -->
      <div v-else>
        <!-- Logged-in state: email form + preview -->
        <template v-if="userState.authenticated">
          <!-- Share Form -->
          <div class="border-b p-6">
            <ShareBetSlip
              :market-name="marketName"
              :items="shareItems"
              :bet-slip-link="betSlipLink"
              @success="handleSuccess"
              @close="handleClose"
            />
          </div>

          <!-- BetSlip Preview -->
          <div class="p-6">
            <div class="flex items-center justify-between mb-6">
              <h5 class="font-semibold text-on-surface">{{ t('share_preview_email_body') }}</h5>
              <Button
                :label="copyPreviewSuccess ? t('copied') : t('copyElectionSlip')"
                :icon="copyPreviewSuccess ? undefined : 'ibi-copy-text'"
                :loading="isCopyingPreview"
                :disabled="copyPreviewSuccess || shareItems.length === 0"
                severity="primary"
                @click="handleCopyPreview"
              />
            </div>
            <div class="border border-outline-variant rounded-lg overflow-hidden bg-surface pointer-events-none">
              <BetSlipByConid v-if="mode === 'conid'" ref="betSlipByConidRef" :conid="conid!" />
              <BetSlipByProduct
                v-else-if="mode === 'product'"
                ref="betSlipByProductRef"
                :product-conid="productConid!"
              />
              <!-- Dummy CTA buttons (inert - for email preview, hidden from assistive tech) -->
              <div class="flex flex-col sm:flex-row gap-2 p-4 border-t" aria-hidden="true">
                <Button :label="t('forward')" :pt="CTA_BUTTON_PT" class="flex-1" inert tabindex="-1" />
                <Button
                  :label="t('viewCurrentOdds')"
                  severity="primary"
                  :pt="CTA_BUTTON_PT"
                  class="flex-1"
                  inert
                  tabindex="-1"
                />
                <Button
                  :label="t('viewElectionBoard')"
                  severity="primary"
                  :pt="CTA_BUTTON_PT"
                  class="flex-1"
                  inert
                  tabindex="-1"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- Not-logged-in state: instructions + copy button + preview -->
        <template v-else>
          <!-- Instructions -->
          <div class="px-6 pt-4 pb-4 text-center">
            <p class="text-on-surface text-xl font-semibold">
              {{ t('shareInstructionText') }}
            </p>
            <p class="text-sm text-on-surface-tertiary mt-1">
              {{ t('shareInstructionSubtext') }}
            </p>
            <div class="mt-3">
              <Button
                :label="copyPreviewSuccess ? t('copied') : t('copyElectionSlip')"
                :icon="copyPreviewSuccess ? undefined : 'ibi-copy-text'"
                :loading="isCopyingPreview"
                :disabled="copyPreviewSuccess || shareItems.length === 0"
                severity="primary"
                @click="handleCopyPreview"
              />
            </div>
          </div>

          <!-- Dashed separator -->
          <div class="mx-6 border-t border-dashed border-outline-variant" />

          <!-- Preview Election Slip -->
          <div class="p-6">
            <h5 class="font-semibold text-on-surface mb-4">{{ t('previewElectionSlip') }}</h5>
            <div class="border border-outline-variant rounded-lg overflow-hidden bg-surface pointer-events-none">
              <BetSlipByConid v-if="mode === 'conid'" ref="betSlipByConidRef" :conid="conid!" />
              <BetSlipByProduct
                v-else-if="mode === 'product'"
                ref="betSlipByProductRef"
                :product-conid="productConid!"
              />
              <!-- Dummy CTA buttons (inert - for email preview, hidden from assistive tech) -->
              <div class="flex flex-col sm:flex-row gap-2 p-4 border-t" aria-hidden="true">
                <Button :label="t('forward')" :pt="CTA_BUTTON_PT" class="flex-1" inert tabindex="-1" />
                <Button
                  :label="t('viewCurrentOdds')"
                  severity="primary"
                  :pt="CTA_BUTTON_PT"
                  class="flex-1"
                  inert
                  tabindex="-1"
                />
                <Button
                  :label="t('viewElectionBoard')"
                  severity="primary"
                  :pt="CTA_BUTTON_PT"
                  class="flex-1"
                  inert
                  tabindex="-1"
                />
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </Dialog>
</template>

<style>
/* Global style needed since Dialog portals to body */
.share-betslip-dialog.p-dialog {
  width: 90vw !important;
  max-width: 56rem !important; /* max-w-4xl */
}
</style>
<!-- last modified by AI tools on 2026-10-07 -->
